import { useState } from 'react';

import { DateField } from '../../../../../components/inputs/DateField';
import { Select } from '../../../../../components/inputs/Select';
import { todayCalendarDate } from '../../../../../components/calendar/monthGrid';
import {
  EMPLOYMENT_TYPE_LABELS,
  type EmploymentType,
  type PersonnelDetail,
} from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';
import { PersonnelSectionModal } from './PersonnelSectionModal';
import { useSectionForm } from './sectionForm';

/**
 * The Employment card's editor — the hire date and the nature of the appointment.
 *
 * **The HMO is not here.** It was, until 15 Sep 2026, when the provider and the card number
 * became their own panel and their own editor — a health card is a benefit rather than a term
 * of appointment, and the wizard was collecting it under Identification while this form
 * corrected it.
 *
 * The employee number is **not** here either. It is assigned on create and quoted elsewhere, so it is
 * shown on the card and not offered as a field: an identifier other records are named after is
 * not a label on this row. A post is not here either — appointing is its own operation with its
 * own endpoint, on the Service Record panel.
 */
export function EditEmploymentModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [dateHired, setDateHired] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType | null>(null);


  function seed() {
    setDateHired(record.dateHired ?? '');
    setEmploymentType(record.employmentType ?? null);
  }

  /* Autogenerate on an EDIT form — allowed since 14 Sep 2026. */
  function generate() {
    const sample = samplePersonnel({
      catalogs: NO_CATALOGS,
      identificationTypes: [],
    });

    setDateHired(sample.dateHired);
    setEmploymentType(sample.employmentType);
  }

  function build() {
    return {
      dateHired: dateHired || null,
      employmentType,
    };
  }

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit employment"
      idPrefix="personnel-employment"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
    >
      <Fields
        dateHired={dateHired}
        setDateHired={setDateHired}
        employmentType={employmentType}
        setEmploymentType={setEmploymentType}
      />
    </PersonnelSectionModal>
  );
}

function Fields({
  dateHired,
  setDateHired,
  employmentType,
  setEmploymentType,
}: {
  dateHired: string;
  setDateHired: (next: string) => void;
  employmentType: EmploymentType | null;
  setEmploymentType: (next: EmploymentType | null) => void;
}) {
  const { saving, error } = useSectionForm();

  return (
    <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id="personnel-employment-date-hired"
          label="Date hired"
          required
          value={dateHired}
          onChange={setDateHired}
          // Employment cannot begin after today; the server allows one day's grace for a
          // timezone edge and refuses anything beyond it.
          max={todayCalendarDate()}
          error={error?.fieldError('dateHired')}
          disabled={saving}
        />

        <Select
          id="personnel-employment-type"
          label="Employment"
          value={employmentType === null ? '' : String(employmentType)}
          onChange={(next) => setEmploymentType(next ? (Number(next) as EmploymentType) : null)}
          options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          sort="none"
          error={error?.fieldError('employmentType')}
          disabled={saving}
        />
    </div>
  );
}
